/**
 * Sessão substituída: a mesma pessoa entrou em outro lugar (conta por licença admite uma sessão por usuário).
 *
 * O servidor avisa de três formas: 401 com `code: SESSION_REPLACED` (qualquer chamada da API e `/api/auth/me`), o evento
 * `session:replaced` do tempo real e o handshake do socket recusado com o mesmo código. Todas chegam aqui: a sessão local
 * é revogada pelo mesmo caminho do `auth:expired` e a tela de login explica o motivo (o aviso sobrevive à navegação).
 *
 * Quem recebe o aviso NÃO chama `/api/auth/logout`: o cookie desta máquina já não vale e, se estiver compartilhado com a
 * sessão nova (mesmo navegador), sair a derrubaria.
 */

export const SESSION_REPLACED_CODE = 'SESSION_REPLACED';

export const SESSION_REPLACED_MESSAGE =
    'Sua conta foi acessada em outro lugar. Por segurança, esta sessão foi encerrada. Entre novamente para continuar.';

const NOTICE_KEY = 'auth:notice';
const NOTICE_SESSION_REPLACED = 'session_replaced';

/** Pede a revogação da sessão local (o AuthContext escuta `auth:expired`) com o motivo. */
export function announceSessionReplaced() {
    window.dispatchEvent(new CustomEvent('auth:expired', { detail: { reason: NOTICE_SESSION_REPLACED } }));
}

/** O `auth:expired` veio de uma sessão substituída? */
export const isSessionReplacedEvent = (event) => event?.detail?.reason === NOTICE_SESSION_REPLACED;

/** Guarda o aviso para a tela de login mostrar depois do redirecionamento (storage indisponível não quebra nada). */
export function rememberSessionReplaced() {
    try {
        sessionStorage.setItem(NOTICE_KEY, NOTICE_SESSION_REPLACED);
    } catch {
        /* sem storage: o login só não mostra o aviso */
    }
}

/**
 * Lê o aviso guardado SEM apagar. Precisa ser idempotente: o React em desenvolvimento (StrictMode) chama o inicializador do
 * estado duas vezes, e uma leitura que apaga deixaria a segunda sem aviso.
 * Devolve a mensagem, ou `null` quando não há.
 */
export function peekSessionNotice() {
    try {
        return sessionStorage.getItem(NOTICE_KEY) === NOTICE_SESSION_REPLACED ? SESSION_REPLACED_MESSAGE : null;
    } catch {
        return null;
    }
}

/** Apaga o aviso: o login o mostra uma vez só (a tela chama isto num efeito, depois de montar). */
export function clearSessionNotice() {
    try {
        sessionStorage.removeItem(NOTICE_KEY);
    } catch {
        /* sem storage: nada a apagar */
    }
}

/** O corpo de erro da API é de sessão substituída? */
export const isSessionReplacedBody = (body) => body?.code === SESSION_REPLACED_CODE;
